import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import { Box, Typography, Table, TableBody, TableCell, TableRow, CircularProgress, Paper } from '@mui/material';
import './SafetyLetterPrintView.css';

const SafetyLetterPrintView = () => {
  const { safetyLetterId, userId } = useParams();
  const [safetyLetter, setSafetyLetter] = useState(null);
  const [signature, setSignature] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch safety letter
        const letters = await apiService.getAllSafetyLetters();
        const letter = letters.find(l => l.id === parseInt(safetyLetterId));
        
        if (!letter) {
          throw new Error('Safety letter not found');
        }
        
        setSafetyLetter(letter);

        // Fetch signatures
        const signatures = await apiService.getSafetyLetterSignatures(safetyLetterId);
        const userSignature = signatures.find(s => s.user_id === parseInt(userId));
        
        if (!userSignature) {
          throw new Error('Signature not found for this user');
        }
        
        setSignature(userSignature);
        
        // Get user info from signature
        setUser({
          id: userSignature.user_id,
          username: userSignature.username,
          email: userSignature.email
        });
        
        setLoading(false);
        
        // Auto-print after a short delay to allow content to render
        setTimeout(() => {
          window.print();
        }, 500);
      } catch (err) {
        console.error('Error fetching safety letter data:', err);
        setError(err.message || 'Failed to load safety letter');
        setLoading(false);
      }
    };

    if (safetyLetterId && userId) {
      fetchData();
    }
  }, [safetyLetterId, userId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !safetyLetter || !signature) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error || 'Safety letter or signature not found'}</Typography>
      </Box>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return `${date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} at ${date.toLocaleTimeString('en-US')}`;
  };

  const monthName = new Date(safetyLetter.year, safetyLetter.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <Box className="print-view" sx={{ p: 4, maxWidth: '8.5in', margin: '0 auto', backgroundColor: 'white' }}>
      <Paper elevation={0} sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4, borderBottom: '3px solid #000', pb: 2 }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
            SAFETY LETTER
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#666' }}>
            {monthName}
          </Typography>
          <Typography variant="h6" sx={{ mt: 1 }}>
            Signature Confirmation
          </Typography>
        </Box>

        {/* Safety Letter Information */}
        <Box sx={{ mb: 4, p: 3, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Safety Letter Details
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%', border: 'none' }}>Title:</TableCell>
                <TableCell sx={{ border: 'none' }}>{safetyLetter.title}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', border: 'none' }}>Period:</TableCell>
                <TableCell sx={{ border: 'none' }}>{monthName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', border: 'none' }}>Created By:</TableCell>
                <TableCell sx={{ border: 'none' }}>{safetyLetter.created_by_username || 'Administrator'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Technician Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '2px solid #000', pb: 1 }}>
            TECHNICIAN INFORMATION
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Name:</TableCell>
                <TableCell>{user.username}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Email:</TableCell>
                <TableCell>{user.email}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Signature Section */}
        <Box sx={{ mb: 4, p: 3, border: '2px solid #000', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
            SIGNATURE ACKNOWLEDGMENT
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              I hereby acknowledge that I have read, understood, and agree to comply with the safety requirements 
              outlined in the safety letter titled "<strong>{safetyLetter.title}</strong>" for the period of <strong>{monthName}</strong>.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              I understand that compliance with these safety requirements is mandatory and failure to comply may 
              result in disciplinary action. I commit to following all safety protocols and procedures as outlined.
            </Typography>
          </Box>

          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #666' }}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%', border: 'none' }}>Signature:</TableCell>
                  <TableCell sx={{ border: 'none', fontSize: '1.2rem', fontFamily: 'cursive', py: 2 }}>
                    {signature.signature}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', border: 'none' }}>Date & Time Signed:</TableCell>
                  <TableCell sx={{ border: 'none' }}>
                    {formatDateTime(signature.signed_at)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* Certification Statement */}
        <Box sx={{ mb: 4, p: 3, bgcolor: '#f9f9f9', border: '1px solid #ddd', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            CERTIFICATION
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic', lineHeight: 1.6 }}>
            This document certifies that the above-named technician has electronically signed and acknowledged 
            the safety letter for the specified period. This signature was captured through the Mascott Vehicle 
            Maintenance Compliance System and represents the technician's commitment to workplace safety and 
            compliance with OSHA regulations.
          </Typography>
        </Box>

        {/* Important Notice */}
        <Box sx={{ mb: 4, p: 2, bgcolor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            ⚠️ IMPORTANT NOTICE
          </Typography>
          <Typography variant="body2">
            This is an official record and should be maintained for compliance purposes. 
            In the event of an OSHA inspection, this document serves as proof of safety training 
            acknowledgment and compliance.
          </Typography>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 5, pt: 3, borderTop: '2px solid #000', textAlign: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Mascott Vehicle Maintenance Compliance System
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            Safety Letter Signature Record
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            Document Generated: {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
            This is a computer-generated document and does not require additional signatures.
          </Typography>
        </Box>

        {/* QR Code or Document ID (Optional) */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Document ID: SL-{safetyLetter.id}-U{user.id}-{safetyLetter.month}-{safetyLetter.year}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default SafetyLetterPrintView;

